import React, { useState, useEffect } from 'react';
import { employeeApi, extractErrorMessage } from '../services/hrApi';
import type { Employee } from '../types';
import OffboardingWizard from './Offboarding/OffboardingWizard';
import ConfirmationModal from '../components/ConfirmationModal';
import { toast } from 'sonner';
import './PageStyles.scss';
import './Onboarding.scss';

const Offboarding: React.FC = () => {
  const [offboardingEmployees, setOffboardingEmployees] = useState<Employee[]>([]);
  const [eligibleEmployees, setEligibleEmployees] = useState<Employee[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [showWizard, setShowWizard] = useState<boolean>(false);
  const [selectedEmployeeId, setSelectedEmployeeId] = useState<string | undefined>(undefined);
  const [showSelectModal, setShowSelectModal] = useState<boolean>(false);
  const [selectedEmployeeForOffboarding, setSelectedEmployeeForOffboarding] = useState<string>('');
  const [loadingEligible, setLoadingEligible] = useState<boolean>(false);
  const [starting, setStarting] = useState<boolean>(false);

  useEffect(() => {
    if (!showWizard) {
      fetchOffboardingEmployees();
    }
  }, [showWizard]);

  const fetchOffboardingEmployees = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Fetch only employees in offboarding process
      const offboardingData = await employeeApi.getEmployees({ status: 'offboarding' });
      const offboardingList = Array.isArray(offboardingData) ? offboardingData : (offboardingData.results || offboardingData.employees || []);
      setOffboardingEmployees(offboardingList);
    } catch (err: any) {
      console.error('Error fetching offboarding employees:', err);
      setError(err?.message || 'Failed to load employees');
    } finally {
      setLoading(false);
    }
  };

  const fetchEligibleEmployees = async () => {
    try {
      setLoadingEligible(true);
      const eligibleData = await employeeApi.getEmployees();
      const allEmployees = Array.isArray(eligibleData) ? eligibleData : (eligibleData.results || eligibleData.employees || []);
      const eligible = allEmployees.filter((emp: Employee) => 
        emp.status === 'active'
      );
      setEligibleEmployees(eligible);
    } catch (err: any) {
      console.error('Error fetching eligible employees:', err);
      toast.error('Failed to load eligible employees');
    } finally {
      setLoadingEligible(false);
    }
  };

  const handleOpenSelectModal = async () => {
    setShowSelectModal(true);
    setSelectedEmployeeForOffboarding('');
    await fetchEligibleEmployees();
  };

  const handleConfirmStartOffboarding = async () => {
    if (!selectedEmployeeForOffboarding) {
      toast.error('Please select an employee');
      return;
    }

    setStarting(true);
    try {
      // Update employee status to 'offboarding'
      await employeeApi.updateEmployee(selectedEmployeeForOffboarding, { status: 'offboarding' });
      
      toast.success('Offboarding process started');
      setShowSelectModal(false);
      setSelectedEmployeeId(selectedEmployeeForOffboarding);
      setSelectedEmployeeForOffboarding('');
      setShowWizard(true);
      
      // Refresh the list
      await fetchOffboardingEmployees();
    } catch (err: any) {
      console.error('Error starting offboarding:', err);
      const errorMessage = extractErrorMessage(err);
      toast.error(`Failed to start offboarding: ${errorMessage}`);
    } finally {
      setStarting(false);
    }
  };

  const getSelectedEmployeeName = () => {
    const employee = eligibleEmployees.find(emp => emp.id === selectedEmployeeForOffboarding);
    return employee ? `${employee.first_name} ${employee.last_name}` : '';
  };

  return (
    <div className="hr-portal-page">
      <div className="hr-portal-page-header">
        <div className="page-header-top">
          <div>
            <h2>Offboarding</h2>
            <p className="page-description">Manage employee offboarding and termination process</p>
          </div>
          {!showWizard && (
            <button
              className="btn-onboard-new"
              onClick={handleOpenSelectModal}
            >
              <i className="bx bx-user-x"></i>
              Offboard an Employee
            </button>
          )}
          {showWizard && (
            <button
              className="btn-back-to-list"
              onClick={() => {
                setShowWizard(false);
                setSelectedEmployeeId(undefined);
              }}
            >
              <i className="bx bx-arrow-back"></i>
              Back to List
            </button>
          )}
        </div>
      </div>
      <div className="hr-portal-page-content">
        {showWizard && selectedEmployeeId ? (
          <OffboardingWizard
            employeeId={selectedEmployeeId}
            onCancel={() => {
              setShowWizard(false);
              setSelectedEmployeeId(undefined);
            }}
            onComplete={() => {
              setShowWizard(false);
              setSelectedEmployeeId(undefined);
              fetchOffboardingEmployees();
            }}
          />
        ) : (
          <>
            {loading && (
              <div className="onboarding-loading">
                <p>Loading employees...</p>
              </div>
            )}

            {error && (
              <div className="onboarding-error">
                <p>{error}</p>
                <button onClick={fetchOffboardingEmployees} className="btn-retry">
                  Retry
                </button>
              </div>
            )}

            {!loading && !error && offboardingEmployees.length > 0 && (
              <>
                <div className="onboarding-summary">
                  <div className="summary-card">
                    <div className="summary-icon">
                      <i className="bx bx-user-x"></i>
                    </div>
                    <div className="summary-content">
                      <h3>{offboardingEmployees.length}</h3>
                      <p>Employees in Offboarding</p>
                    </div>
                  </div>
                </div>

                <div className="onboarding-employees-grid">
                  {offboardingEmployees.map((employee) => (
                    <div 
                      key={employee.id} 
                      className="onboarding-employee-card clickable"
                      onClick={() => {
                        setSelectedEmployeeId(employee.id);
                        setShowWizard(true);
                      }}
                    >
                      <div className="employee-card-header">
                        <div className="employee-avatar">
                          {employee.first_name?.[0]?.toUpperCase() || 'E'}
                          {employee.last_name?.[0]?.toUpperCase() || ''}
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <span className="status-badge status-onboarding">
                            Offboarding
                          </span>
                        </div>
                      </div>
                      <div className="employee-card-body">
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                          <h3 className="employee-name">
                            {employee.first_name} {employee.last_name}
                          </h3>
                          <i className="bx bx-chevron-right" style={{ fontSize: '24px', color: '#0dcaf0' }}></i>
                        </div>
                        <p className="employee-id">ID: {employee.employee_id}</p>
                        <div className="employee-info">
                          <div className="info-item">
                            <i className="bx bx-envelope"></i>
                            <span>{employee.email}</span>
                          </div>
                          {employee.phone && (
                            <div className="info-item">
                              <i className="bx bx-phone"></i>
                              <span>{employee.phone}</span>
                            </div>
                          )}
                          <div className="info-item">
                            <i className="bx bx-briefcase"></i>
                            <span>{employee.position}</span>
                          </div>
                          <div className="info-item">
                            <i className="bx bx-building"></i>
                            <span>{employee.department}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}

            {!loading && !error && offboardingEmployees.length === 0 && (
              <div className="onboarding-empty">
                <i className="bx bx-user-x"></i>
                <p>No employees currently in offboarding</p>
              </div>
            )}
          </>
        )}
      </div>

      {/* Employee Selection Modal */}
      {showSelectModal && (
        <div className="confirmation-modal-overlay" onClick={() => !starting && setShowSelectModal(false)}>
          <div className="confirmation-modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '600px' }}>
            <div className="confirmation-modal-header">
              <h3>Select Employee to Offboard</h3>
              <button
                className="btn-close-modal"
                onClick={() => setShowSelectModal(false)}
                disabled={starting}
              >
                <i className="bx bx-x"></i>
              </button>
            </div>
            <div className="confirmation-modal-body" style={{ textAlign: 'left' }}>
              {loadingEligible ? (
                <div style={{ textAlign: 'center', padding: '40px' }}>
                  <i className="bx bx-loader-alt bx-spin" style={{ fontSize: '32px', color: '#0dcaf0' }}></i>
                  <p style={{ marginTop: '16px', color: '#6c757d' }}>Loading eligible employees...</p>
                </div>
              ) : eligibleEmployees.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '40px' }}>
                  <i className="bx bx-info-circle" style={{ fontSize: '48px', color: '#6c757d', marginBottom: '16px' }}></i>
                  <p style={{ color: '#6c757d' }}>No eligible employees found. Employees must have status 'active' to start offboarding.</p>
                </div>
              ) : (
                <>
                  <p style={{ marginBottom: '16px', color: '#495057', fontSize: '14px' }}>
                    Select an employee to start the offboarding process. This will update their status to 'offboarding' and open the offboarding wizard.
                  </p>
                  <div style={{ marginBottom: '20px' }}>
                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: 500, color: '#212529' }}>
                      Employee
                    </label>
                    <select
                      value={selectedEmployeeForOffboarding}
                      onChange={(e) => setSelectedEmployeeForOffboarding(e.target.value)}
                      disabled={starting}
                      style={{
                        width: '100%',
                        padding: '10px 12px',
                        border: '1px solid #dee2e6',
                        borderRadius: '6px',
                        fontSize: '14px',
                        backgroundColor: starting ? '#f8f9fa' : '#fff',
                        cursor: starting ? 'not-allowed' : 'pointer',
                      }}
                    >
                      <option value="">-- Select an employee --</option>
                      {eligibleEmployees.map((employee) => (
                        <option key={employee.id} value={employee.id}>
                          {employee.first_name} {employee.last_name} ({employee.employee_id}) - {employee.status.charAt(0).toUpperCase() + employee.status.slice(1)}
                        </option>
                      ))}
                    </select>
                  </div>
                </>
              )}
            </div>
            <div className="confirmation-modal-footer">
              <button
                className="btn-cancel-confirm"
                onClick={() => setShowSelectModal(false)}
                disabled={starting}
              >
                Cancel
              </button>
              <button
                className="btn-confirm-warning"
                onClick={handleConfirmStartOffboarding}
                disabled={starting || !selectedEmployeeForOffboarding || loadingEligible || eligibleEmployees.length === 0}
              >
                {starting ? (
                  <>
                    <i className="bx bx-loader-alt bx-spin"></i>
                    Starting...
                  </>
                ) : (
                  <>
                    <i className="bx bx-user-x"></i>
                    Start Offboarding
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Offboarding;
